import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Feature, FeatureCollection, MultiPolygon, Point, Polygon } from "geojson";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import type { Job } from "../../../models";
import {
	JobMapProps,
	JobMapViewModel,
	PopupData,
	PolygonMetadata,
	PolygonRenderData,
	MarkerRenderData,
	DEFAULT_CENTER,
	DEFAULT_ZOOM,
} from "./JobMap.types";
import { getJobColor, getShapeFeatureProperty, parseChangeInfo, isMarkerVisible } from "./JobMap.helpers";

const SELECTED_COLOR = "#ff0000";
const SELECTED_OPACITY = 0.6;

export function useJobMapViewModel(props: JobMapProps): JobMapViewModel {
	const [mapPoints, setMapPoints] = useState<MarkerRenderData[]>([]);
	const [mapPolygons, setMapPolygons] = useState<PolygonRenderData[]>([]);
	const [visibleMapMarkers, setVisibleMapMarkers] = useState<MarkerRenderData[]>([]);
	const [visibleMapPolygons, setVisibleMapPolygons] = useState<PolygonRenderData[]>([]);
	const [center] = useState<google.maps.LatLngLiteral>(DEFAULT_CENTER);
	const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);
	const [bounds, setBounds] = useState<google.maps.LatLngBoundsLiteral | undefined>(undefined);
	const [activePopup, setActivePopup] = useState<PopupData | null>(null);
	const [polygonMetadata, setPolygonMetadata] = useState<PolygonMetadata[]>([]);

	// Refs for lasso drawing state (avoids React re-renders during drawing)
	const lassoPathRef = useRef<google.maps.LatLngLiteral[]>([]);
	const completeLassoRef = useRef<() => void>(() => {});

	const mapsLibrary = useMapsLibrary("maps");
	const geometryLibrary = useMapsLibrary("geometry");
	const map = useMap();

	const selectedFeatureIdsKey = useMemo(() => {
		if (!props.selectedFeatureIds || props.selectedFeatureIds.size === 0) return "";
		return Array.from(props.selectedFeatureIds).sort().join(",");
	}, [props.selectedFeatureIds]);

	// Effect 1: Process job geodata
	useEffect(() => {
		if (!map || !mapsLibrary) return;

		const mapBounds = new google.maps.LatLngBounds();
		const allPolygons: PolygonRenderData[] = [];
		const allPoints: MarkerRenderData[] = [];
		const allMetadata: PolygonMetadata[] = [];

		// Helper function to process a polygon/multipolygon feature collection
		function processPolygonFeatures(
			featureCollection: FeatureCollection<Polygon> | FeatureCollection<MultiPolygon>,
			job: Job,
			jobId: string,
			typePrefix: string,
		) {
			featureCollection.features?.forEach((feature: Feature<Polygon | MultiPolygon | Point>, featureIndex) => {
				if (!feature) return;

				if (feature.geometry.type !== "Polygon" && feature.geometry.type !== "MultiPolygon") {
					return;
				}

				if (feature.geometry.bbox) {
					mapBounds.extend(new google.maps.LatLng(feature.geometry.bbox[1], feature.geometry.bbox[0]));
					mapBounds.extend(new google.maps.LatLng(feature.geometry.bbox[3], feature.geometry.bbox[2]));
				}

				const coordinates =
					feature.geometry.type === "Polygon" ? [feature.geometry.coordinates] : feature.geometry.coordinates;
				const paths = coordinates.map((coordinate) => {
					return coordinate
						.map((c) => {
							return c
								.map((cc) => {
									return { lat: cc[1], lng: cc[0] };
								})
								.flat();
						})
						.flat();
				});

				paths.forEach((path, pathIndex) => {
					const polygonId = `${typePrefix}-${jobId}-${featureIndex}-${pathIndex}`;
					const isSelected = props.selectedFeatureIds?.has(polygonId);

					allMetadata.push({ id: polygonId, paths: path });

					allPolygons.push({
						id: polygonId,
						paths: path,
						fillOpacity: isSelected ? SELECTED_OPACITY : 0.4,
						fillColor: isSelected ? SELECTED_COLOR : getJobColor(jobId),
						strokeColor: isSelected ? SELECTED_COLOR : undefined,
						job,
					});
				});
			});
		}

		// Helper function to process point features
		function processPointFeatures(featureCollection: FeatureCollection<Point>, jobId: string) {
			featureCollection.features?.forEach((feature: Feature<Point>, featureIndex) => {
				if (feature.geometry.type !== "Point") {
					return;
				}
				const sampleId = getShapeFeatureProperty(feature, "spl id");
				const barcode = getShapeFeatureProperty(feature, "spl brcd");
				const change = getShapeFeatureProperty(feature, "change");
				const { hasChange, changeType, changeReason } = parseChangeInfo(change);
				const pointPosition = { lat: feature.geometry.coordinates[1], lng: feature.geometry.coordinates[0] };
				mapBounds.extend(new google.maps.LatLng(pointPosition.lat, pointPosition.lng));

				const pointId = `points-${jobId}-${featureIndex}`;
				const isSelected = props.selectedFeatureIds?.has(pointId);

				// Add point to metadata for lasso selection (single point as path)
				allMetadata.push({ id: pointId, paths: [pointPosition] });

				allPoints.push({
					id: pointId,
					position: pointPosition,
					iconColor: isSelected ? "error" : hasChange ? "warning" : undefined,
					sampleId,
					barcode,
					hasChange,
					changeType,
					changeReason,
				});
			});
		}

		// Process each job's geodata
		props.jobs.forEach((job) => {
			const jobId = job.id || "unknown";

			// Process boundary
			const boundary = job.files?.boundaryGeoJSON as
				| FeatureCollection<Polygon>
				| FeatureCollection<MultiPolygon>
				| undefined;
			if (boundary) {
				processPolygonFeatures(boundary, job, jobId, "boundary");
			}

			// Process zones
			const zones = job.files?.zonesGeoJSON as FeatureCollection<Polygon> | FeatureCollection<MultiPolygon> | undefined;
			if (zones) {
				processPolygonFeatures(zones, job, jobId, "zones");
			}

			// Process points
			const points = job.files?.pointsGeoJSON as FeatureCollection<Point> | undefined;
			if (points) {
				processPointFeatures(points, jobId);
			}
		});

		setMapPoints(allPoints);
		setMapPolygons(allPolygons);
		setPolygonMetadata(allMetadata);

		if (map && props.jobs.length > 0 && (allPolygons.length > 0 || allPoints.length > 0)) {
			// Wait for the map to be fully rendered before fitting bounds
			// This is necessary when the map is inside a Dialog or other dynamically-sized container
			const idleListener = google.maps.event.addListenerOnce(map, "idle", () => {
				map.fitBounds(mapBounds);
			});

			// Also try fitting bounds after a delay as a fallback
			const timeoutId = setTimeout(() => {
				map.fitBounds(mapBounds);
			}, 200);

			return () => {
				google.maps.event.removeListener(idleListener);
				clearTimeout(timeoutId);
			};
		}
	}, [props.jobs, selectedFeatureIdsKey, props.lassoMode, map, mapsLibrary]);

	// Effect 2: Update visible markers based on bounds and zoom
	useEffect(() => {
		if (!map || !mapsLibrary) return;

		const currentBounds = map.getBounds();
		if (!currentBounds) return;

		const currentZoom = map.getZoom();

		// Filter mapPoints to only those in bounds and above zoom threshold
		const filteredMarkers = mapPoints.filter((marker) => {
			return isMarkerVisible(marker.position, currentBounds, currentZoom, props.zoomLevelToDisplayPoints);
		});

		setVisibleMapMarkers(filteredMarkers);
		setVisibleMapPolygons(mapPolygons);
	}, [zoom, bounds, map, mapsLibrary, mapPoints, mapPolygons, props.zoomLevelToDisplayPoints]);

	// Handlers
	const onBoundsChanged = (newBounds: google.maps.LatLngBoundsLiteral) => {
		setBounds(newBounds);
	};

	const onZoomChanged = (newZoom: number) => {
		setZoom(newZoom);
	};

	// Complete the lasso selection - check which features are inside the drawn polygon
	const completeLasso = useCallback(() => {
		const currentPath = lassoPathRef.current;

		if (!geometryLibrary || currentPath.length < 3) {
			lassoPathRef.current = [];
			return;
		}

		// Check which features are entirely contained in the lasso (all vertices inside)
		const lassoPolygon = new google.maps.Polygon({ paths: currentPath });
		const selectedIds = new Set<string>();

		polygonMetadata.forEach((meta) => {
			const isFullyInside = meta.paths.every((point) => {
				return geometryLibrary.poly.containsLocation(new google.maps.LatLng(point.lat, point.lng), lassoPolygon);
			});
			if (isFullyInside) {
				selectedIds.add(meta.id);
			}
		});

		// Notify parent and clear path
		props.onLassoComplete?.(selectedIds);
		lassoPathRef.current = [];
	}, [polygonMetadata, geometryLibrary, props]);

	// Keep completeLassoRef always pointing to latest completeLasso
	completeLassoRef.current = completeLasso;

	const onMapClick = useCallback(() => {
		// Lasso mode clicks are handled by native event listeners
		if (props.lassoMode) {
			return;
		}

		// Normal click behavior
		setActivePopup(null);
		window.postMessage({ type: "closeInfoWindow", source: "map-click" }, "*");
	}, [props.lassoMode]);

	const onInfoWindowClose = () => {
		setActivePopup(null);
	};

	const onPolygonClick = useCallback((job: Job, position: google.maps.LatLngLiteral) => {
		setActivePopup({ job, position });
	}, []);

	// Point-and-click lasso: click to add vertices, click near first point to close
	useEffect(() => {
		if (!map || !props.lassoMode) return;

		map.setOptions({ draggable: false });

		// Create native polyline for lasso visualization
		const lassoPolyline = new google.maps.Polyline({
			map,
			strokeColor: "#2196f3",
			strokeWeight: 2,
			strokeOpacity: 0.8,
		});

		// Create a marker for the first point (click target to close polygon)
		let firstPointMarker: google.maps.Marker | null = null;

		const handleClick = (e: google.maps.MapMouseEvent) => {
			if (!e.latLng) return;

			const clickedPoint = e.latLng.toJSON();

			// If we have 3+ points and click near the first point, close the polygon
			if (lassoPathRef.current.length >= 3) {
				const firstPoint = lassoPathRef.current[0];
				const distance = Math.sqrt(
					Math.pow(clickedPoint.lat - firstPoint.lat, 2) + Math.pow(clickedPoint.lng - firstPoint.lng, 2),
				);
				// Close if within ~0.0005 degrees (~50m at equator, scales with zoom)
				const zoom = map.getZoom() || 10;
				const threshold = 0.01 / Math.pow(2, zoom - 10); // Smaller threshold at higher zoom
				if (distance < threshold) {
					completeLassoRef.current();
					return;
				}
			}

			// Add the point
			lassoPathRef.current.push(clickedPoint);
			lassoPolyline.setPath(lassoPathRef.current);

			// Create/update first point marker
			if (lassoPathRef.current.length === 1) {
				firstPointMarker = new google.maps.Marker({
					position: clickedPoint,
					map,
					icon: {
						path: google.maps.SymbolPath.CIRCLE,
						scale: 8,
						fillColor: "#2196f3",
						fillOpacity: 1,
						strokeColor: "#ffffff",
						strokeWeight: 2,
					},
					title: "Click to close polygon",
				});
				// Clicking the marker closes the polygon
				firstPointMarker.addListener("click", () => {
					if (lassoPathRef.current.length >= 3) {
						completeLassoRef.current();
					}
				});
			}
		};

		const clickListener = map.addListener("click", handleClick);

		return () => {
			google.maps.event.removeListener(clickListener);
			lassoPolyline.setMap(null);
			if (firstPointMarker) firstPointMarker.setMap(null);
			map.setOptions({ draggable: true });
			lassoPathRef.current = [];
		};
	}, [map, props.lassoMode]);

	return {
		// State
		mapPoints,
		mapPolygons,
		visibleMapMarkers,
		visibleMapPolygons,
		center,
		zoom,
		bounds,
		activePopup,
		mapsLibraryLoaded: !!mapsLibrary,
		polygonMetadata,

		// Handlers
		onBoundsChanged,
		onZoomChanged,
		onMapClick,
		onInfoWindowClose,
		onPolygonClick,
	};
}
